<i18n
  src="@/locales/components/dashboard/agro_exporter/container_outcomes.yml"
  lang="yaml"
></i18n>
<template>
  <div class="bg-white shadow rounded-lg p-4 sm:p-6 xl:p-8">
    <div class="mb-4 flex justify-between">
      <div>
        <h3 class="text-xl font-bold text-gray-900 mb-2">
          {{ $t("labels.title") }}
        </h3>
        <span class="text-base font-normal text-gray-500">
          {{ $t("labels.description") }}
        </span>
      </div>
      <div class="flex-shrink-0">
        <router-link
          class="text-sm font-medium text-lime-600 hover:text-lime-700 cursor-pointer hover:bg-gray-100 rounded-lg p-2"
          :to="{
            name: routeName(routePermission, 'ae_finances'),
            params: {
              agro_exporter_id: agro_exporter_id
            }
          }"
        >
          {{ $t("labels.show_link") }}
        </router-link>
      </div>
    </div>
    <div class="flex flex-col mt-8">
      <div :class="guiGridRow">
        <template v-for="(chart, index) in summary_by_currencies" :key="index">
          <div :id="index" class="flex justify-center">
            <apexchart
              type="radialBar"
              width="200"
              height="200"
              :options="chartOptions(chart)"
              :series="[chart.percent_receivable || 0]"
            ></apexchart>
            <div class="flex flex-col justify-center -ml-5">
              <span class="text-red-600">{{ $t("labels.payable") }}</span>
              <b class="text-lg text-red-600" v-if="chart.total_amount && chart.total_amount_paid">
                {{ currencyMoney(chart.total_amount - chart.total_amount_paid, chart.symbol) }}
              </b>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="flex flex-col mt-8">
      <div class="overflow-x-auto rounded-lg">
        <div class="align-middle inline-block min-w-full">
          <div class="shadow overflow-hidden sm:rounded-lg">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th
                    scope="col"
                    class="px-2 py-4 text-left text-base font-medium text-gray-700"
                    v-if="summary_by_currencies.length > 1"
                  >
                    {{ $t("labels.currency") }}
                  </th>
                  <th
                    scope="col"
                    class="px-2 py-4 text-left text-base font-medium text-gray-700"
                  >
                    {{ $t("labels.total_num_invoices") }}
                  </th>
                  <th
                    scope="col"
                    class="px-2 py-4 text-left text-base font-medium text-gray-700"
                  >
                    {{ $t("labels.percent_receivable") }}
                  </th>
                  <th
                    scope="col"
                    class="px-2 py-4 text-left text-base font-medium text-gray-700"
                  >
                    {{ $t("labels.total_amount_paid") }}
                  </th>
                  <th
                    scope="col"
                    class="px-2 py-4 text-left text-base font-medium text-gray-700"
                  >
                    {{ $t("labels.total_amount") }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white table-body">
                <tr
                  v-for="(summary, index) in summary_by_currencies"
                  :key="index"
                  class="table-row"
                >
                  <td
                    class="p-3 whitespace-nowrap text-sm font-normal text-gray-900"
                    v-if="summary_by_currencies.length > 1"
                  >
                    {{ summary.name }}
                  </td>
                  <td
                    class="p-3 whitespace-nowrap text-sm font-normal text-gray-900"
                  >
                    {{ summary.total_num_invoices }}
                  </td>
                  <td
                    class="p-3 whitespace-nowrap text-sm font-normal text-gray-900"
                  >
                    {{ summary.percent_receivable }} %
                  </td>
                  <td
                    class="p-3 whitespace-nowrap text-sm font-normal text-gray-900"
                  >
                    {{ currencyMoney(summary.total_amount_paid, summary.symbol)}}
                  </td>
                  <td
                    class="p-3 whitespace-nowrap text-sm font-normal text-gray-900"
                  >
                    {{ currencyMoney(summary.total_amount, summary.symbol) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import Requester from "@/components/views/dashboard/requesters/agro_exporter/container_outcomes";
export default {
  name: "DashboardAEContainerOutcomes",
  components: {
    apexchart: VueApexCharts
  },
  data() {
    return {
      summary_by_currencies: {},
      load_general_data: false
    };
  },
  props: {
    agro_exporter_id: { required: true }
  },
  created() {
    this.fetchSummary();
  },
  computed: {
    routePermission() {
      if (
        this.checkIfUserIsAuthorized(
          "finances",
          "container_outcomes",
          "agro_exporter"
        )
      )
        return "container_outcomes";
      else if (
        this.checkIfUserIsAuthorized(
          "finances",
          "query_container_outcomes",
          "agro_exporter"
        )
      )
        return "query_container_outcomes";
      else return "";
    },
    guiGridRow() {
      if (this.summary_by_currencies.length >= 2) return "grid grid-cols-2";
      else return `grid grid-cols-${this.summary_by_currencies.length}`;
    }
  },
  methods: {
    async fetchSummary() {
      const response = await Requester.fetchSummary(this.agro_exporter_id);
      this.summary_by_currencies = response.data;
      this.load_general_data = true;
    },
    chartOptions(chart) {
      return {
        chart: {
          type: "radialBar"
        },
        plotOptions: {
          radialBar: {
            hollow: { size: 55 },
            dataLabels: {
              name: {
                offsetY: 80,
                fontSize: "16px"
              },
              value: {
                offsetY: -5,
                color: "#65A30D",
                formatter: val => {
                  return parseInt(val)+"%";
                },
                fontSize: "32px"
              }
            }
          }
        },
        labels: [this.$t("labels.chart_label", { currency: chart.symbol })],
        colors: ["#65A30D"]
      };
    }
  }
};
</script>
